import type { VidRangeData } from "~/units/scripture";
import { createVerse, createVidRange, rangesOverlap } from "~/units/scripture";
import type {
  CommentaryGroupData,
  CommentarySearchResultsDoc,
  CommentarySearchResultsGroup,
  FlatCommentaryGroup,
  ProcessedCommentaryBase,
  ProcessedCommentaryComment,
  ProcessedCommentaryImage,
} from "./types";
import { getCommentaryTypeOrFallback } from "~/static/taxonomy/commentaryTypes";
import { parseJSON, parseJsonItemsAndSkipFailures } from "~/units/json";
import { getDetailLevelFromWeightWithFallback } from "~/static/taxonomy/detailLevels";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import { InvalidCommentaryGroupError } from "~/errors/classes";
import type { StandaloneVerseData } from "@rsc/scripture-model";
import { getErrorMessage } from "~/errors/util";

/**
 * Since Solr can't do grouping on a multi-value field, we had to make the `ss_computed_group` field a single-value
 * field. However, some comments may belong to multiple groups, so the `ss_computed_group` field may contain a JSON
 * string representing more than one commentary group. These need to be post-processed to redistribute their comments
 * into other groups.
 *
 * @param solrGroups
 *   The groups to re-group.
 * @param queryRange
 *   The range of the query. This is used to re-filter the groups after re-grouping,
 *   to remove groups that are outside the original query range.
 *
 * @returns
 *   A result object containing the re-grouped groups, or an error if some of the group data is invalid.
 */
export function reGroupCommentarySearchResults(
  solrGroups: CommentarySearchResultsGroup[],
  queryRange: { fromVid: number; toVid: number },
): Result<
  {
    doclist: CommentarySearchResultsGroup["doclist"];
    group: CommentaryGroupData;
  }[],
  InvalidCommentaryGroupError
> {
  const results = new Map<
    string,
    {
      doclist: CommentarySearchResultsGroup["doclist"];
      group: CommentaryGroupData;
    }
  >();

  for (const solrGroup of solrGroups) {
    const parsedGroupValue = parseCommentaryGroupData(solrGroup.groupValue);
    if (parsedGroupValue.isErr()) {
      return err(parsedGroupValue.error);
    }

    for (const groupValue of parsedGroupValue.value) {
      // Group by semantic identity, not serialized property order or verse text.
      // Null and omitted neutral translation fields represent the same group.
      const reference = groupValue.range ?? groupValue.verse;
      const jsonGroupValue = JSON.stringify([
        groupValue.text ?? null,
        groupValue.verse ? "verse" : groupValue.range ? "range" : "text",
        reference?.fromVid ?? null,
        reference?.toVid ?? null,
        reference?.tid ?? null,
        reference?.tr ?? null,
        groupValue.versePart ?? null,
      ]);

      const groupRange = groupValue.range ?? groupValue.verse;
      if (groupRange) {
        const groupRangeObject = createVidRange(groupRange);
        if (!rangesOverlap(groupRangeObject, queryRange)) {
          // This group falls outside the query range. This can happen, e.g., when a comment is attached to both
          // Mark 1:2 and Malachi 3:1, and the query is for Mark 1:2. Then we don't want to include the Malachi group.
          continue;
        }
      }

      if (results.has(jsonGroupValue)) {
        // Merge the doc lists.
        const existingGroup = results.get(jsonGroupValue)!;
        results.set(jsonGroupValue, {
          doclist: {
            docs: [...existingGroup.doclist.docs, ...solrGroup.doclist.docs],
            numFound:
              existingGroup.doclist.numFound + solrGroup.doclist.numFound,
            numFoundExact:
              existingGroup.doclist.numFoundExact &&
              solrGroup.doclist.numFoundExact,
            start: Math.min(
              existingGroup.doclist.start,
              solrGroup.doclist.start,
            ),
          },
          group: existingGroup.group,
        });
      } else {
        results.set(jsonGroupValue, {
          doclist: solrGroup.doclist,
          group: groupValue,
        });
      }
    }
  }

  return ok([...results.values()]);
}

export function processCommentarySearchResultsGroup(
  doclist: CommentarySearchResultsGroup["doclist"],
  group: CommentaryGroupData,
): FlatCommentaryGroup {
  return {
    heading: group.text,
    range: group.range ? createVidRange(group.range) : undefined,
    verse: group.verse ? createVerse(group.verse) : undefined,
    versePart: group.versePart,
    doclist: {
      docs: doclist.docs.map(processCommentarySearchResultsDoc),
      numFound: doclist.numFound,
      numFoundExact: doclist.numFoundExact,
      start: doclist.start,
    },
  };
}

function processCommentarySearchResultsDoc(
  doc: CommentarySearchResultsDoc,
): ProcessedCommentaryComment | ProcessedCommentaryImage {
  const common: ProcessedCommentaryBase = {
    nid: doc.its_nid || 0,
    status: doc.bs_status,
    ranges: parseJsonItemsAndSkipFailures<VidRangeData>(
      doc.zm_scripture ?? [],
    ).map(createVidRange),
    detailLevel: getDetailLevelFromWeightWithFallback(
      doc.its_detail_weight ?? 0,
    ),
    commentaryType: getCommentaryTypeOrFallback(
      // TODO: What do we do with multiple commentary types?
      doc.sm_type_machine_name?.[0] || "",
    ),
    weight: doc.its_weight || 0,
    language: doc.ss_search_api_language,
  };

  switch (doc.index_id) {
    case "commentary_comments":
      return {
        ...common,
        type: "comment",
        heading: doc.ss_heading,
        teaser: doc.zs_body_teaser,
        flags: doc.sm_flag_names || [],
      };

    case "commentary_images":
      return {
        ...common,
        type: "image",
        title: doc.ss_title || "",
        description: doc.tm_X3b_en_description?.[0] || "",
        styles: {
          original: doc.zs_original_url || "",
          large: doc.zs_large_url || "",
          small: doc.zs_small_url || "",
          placeholder: doc.zs_placeholder_base64 || "",
        },
      };
  }

  throw new Error("Unknown index_id in solr result.");
}

function parseCommentaryGroupData(
  jsonData: string,
): Result<CommentaryGroupData[], InvalidCommentaryGroupError> {
  return parseJSON(jsonData)
    .mapErr(
      (e) =>
        new InvalidCommentaryGroupError(
          `Failed to parse JSON: ${getErrorMessage(e)}`,
          jsonData,
        ),
    )
    .map((parsed: unknown) => {
      if (!Array.isArray(parsed)) {
        throw new InvalidCommentaryGroupError("Expected an array.", jsonData);
      }

      const filtered = parsed.filter((item) => isCommentaryGroupData(item));

      if (filtered.length !== parsed.length) {
        throw new InvalidCommentaryGroupError(
          `${parsed.length - filtered.length} items failed validation.`,
          jsonData,
        );
      }

      return filtered;
    });
}

function isCommentaryGroupData(data: unknown): data is CommentaryGroupData {
  if (typeof data !== "object" || data === null) {
    return false;
  }

  const obj = data as Record<string, unknown>;

  return (
    (obj.text === undefined || typeof obj.text === "string") &&
    (obj.range === undefined || isVidRangeData(obj.range)) &&
    (obj.verse === undefined || isStandaloneVerseData(obj.verse)) &&
    (obj.versePart === undefined || typeof obj.versePart === "string")
  );
}

function isVidRangeData(data: unknown): data is VidRangeData {
  if (typeof data !== "object" || data === null) {
    return false;
  }

  const obj = data as Record<string, unknown>;

  return (
    typeof obj.fromVid === "number" &&
    typeof obj.toVid === "number" &&
    ((obj.tid == null && obj.tr == null) ||
      (typeof obj.tid === "string" && typeof obj.tr === "string" && !!obj.tr))
  );
}

function isStandaloneVerseData(data: unknown): data is StandaloneVerseData {
  if (typeof data !== "object" || data === null) {
    return false;
  }

  const obj = data as Record<string, unknown>;

  return (
    typeof obj.fromVid === "number" &&
    typeof obj.toVid === "number" &&
    typeof obj.tid === "string" &&
    typeof obj.tr === "string" &&
    !!obj.tr &&
    typeof obj.chapterNumber === "number" &&
    Array.isArray(obj.parts) &&
    typeof obj.bid === "string" &&
    typeof obj.text === "string"
  );
}
